'use client';

import { useTealium } from '@fshr/context/tealium-context';

type BaseRecoEvent = {
	placement: string;
	position?: number;
};

type PropertyRecoClick = BaseRecoEvent & {
	propertyCode: string;
	propertyName: string;
	propertyDatalakeRecoId: string;
};

type EnhancementRecoClick = BaseRecoEvent & {
	sku?: string;
	name: string;
	propertyCode: string;
	propertyName: string;
	enhancementDatalakeRecoId: string;
};

/* ===========================
   Helpers
=========================== */

export const getUtahValue = (key: string) =>
	window?.utag?.data?.[key] ? (window?.utag?.data[key] as string).toLowerCase() : ['na'];

const mapCommonRecoFields = ({ placement, position }: BaseRecoEvent) => ({
	reco_placement: [placement],
	reco_position: [String(position ?? 'na')],
});

/* ===========================
   Hook
=========================== */

export const useRecoTracking = () => {
	const { trackLink, addToRecommendationsCookie } = useTealium();

	return {
		/* ===========================
		   For You panel open
		=========================== */
		trackRecoVisit: (placement: string) => {
			trackLink('for_you_view', {
				property_name: getUtahValue('property_name'),
				property_region: getUtahValue('property_region'),
			});
		},

		/* ===========================
		   Property recommendation click
		=========================== */
		trackPropertyRecoClick: (reco: PropertyRecoClick) => {
			addToRecommendationsCookie({
				property: reco.propertyCode,
				datalakeRecoId: [
					{
						sku: '',
						datalakeRecoId: reco.propertyDatalakeRecoId ?? 'na',
						ors: reco.propertyCode ?? 'na',
						type: 'property',
					},
				],
			});

			trackLink('reco_click', {
				...mapCommonRecoFields(reco),
				reco_property_name: [reco.propertyCode || getUtahValue('property_code') || 'na'],
				reco_property_code: [reco.propertyCode || getUtahValue('property_code') || 'na'],
				reco_room_code_array: ['na'],
				reco_room_package_code_array: ['na'],
				property_datalake_reco_id: [reco.propertyDatalakeRecoId],
			});
		},

		/* ===========================
		   Enhancement recommendation click
		=========================== */
		trackEnhancementRecoClick: (reco: EnhancementRecoClick) => {
			addToRecommendationsCookie({
				ancillary: reco?.sku,
				datalakeRecoId: [
					{
						sku: reco?.sku ?? 'na',
						datalakeRecoId: reco.enhancementDatalakeRecoId ?? 'na',
						ors: reco.propertyCode ?? 'na',
						type: 'ancillary',
					},
				],
			});
			trackLink('reco_click', {
				...mapCommonRecoFields(reco),
				reco_enhancement_property_name: [reco?.propertyCode ?? 'na'],
				reco_enhancement_sku: [reco?.sku ?? 'na'],
				reco_enhancement_name: [reco?.name ?? 'na'],
				enhancement_datalake_reco_id: [reco?.enhancementDatalakeRecoId],
			});
		},
	};
};
